import { createApp, ref, watch, computed, reactive, onMounted } from 'vue';
import { vComma } from './directives/comma.js';
import { createVuetify, useTheme, useDisplay } from 'vuetify';
import { aliases, mdi } from '/lib/vuetify/lib/iconsets/mdi.mjs';
import { layer, isMobile } from './_globaldata.js';
import { storageManager, debounce } from './_utils.js';
import { useCookies } from 'vue3-cookies';
import lang from "./translate/lang.js";

const vuetifyOptions = {
	defaults: {
		global: {
			ripple: false,
		},
	},
	theme: {
		defaultTheme: 'light',
		themes: {
			light: {
				colors: {
					'za-text_primary': '#1e1e23',
					'za-trading_bg': '#e7e9ed',
					'za-container_bg': '#ffffff',
					'za-btn_brand_bg': '#ffc700',
					'za-btn_preview_bg': '#19c719',
					'za-input_type04_bg_default': '#fafbfb',
					'za-input_type03_brd_default': '#e1e1e1',
					'za-input_brand_brd_focus': '#ffc700',
					'za-brand': '#ffc700',
					'za-icon_type02': '#7d7e85',
				},
			},
			dark: {
				colors: {
					'za-text_primary': '#ffffff',
					'za-trading_bg': '#000000',
					'za-container_bg': '#1f1d24',
					'za-btn_brand_bg': '#f2f219',
					'za-btn_preview_bg': '#19f219',
					'za-input_type04_bg_default': '#1f1d24',
					'za-input_type03_brd_default': '#50505b',
					'za-input_brand_brd_focus': '#f2f219',
					'za-brand': '#f2f219',
					'za-icon_type02': '#7d7e85',
				},
			},
		},
	},
	icons: {
		defaultSet: 'mdi',
		aliases,
		sets: {
			mdi,
		},
	},
};
const vuetify = createVuetify(vuetifyOptions);

// 폼 POST 로그인 실패 시 서버가 /Login 으로 되돌리며 넘긴 사유·아이디. Vue 가 화면을 바꾸기 전에 서버가 그린 원본에서 읽어 둔다
const loginErrorEl = document.getElementById('loginError');
const loginErrorMessage = loginErrorEl?.dataset.message || '';
const loginErrorUserId = loginErrorEl?.dataset.userid || '';
const visibleDownload = ref(true);
const closeDownload = () => {
	visibleDownload.value = false;
};

createApp({
	setup() {
		const { mobile } = useDisplay();
		const { cookies } = useCookies();
		isMobile.value = mobile.value;
		
		const logoUrl = ref('../images/logo_dark.png');
		//#region 主题切换、
		const theme = useTheme();
		const themeIcons = ['mdi-weather-night', 'mdi-weather-sunny', 'mdi-theme-light-dark'];
		const currentTheme = ref('light');
		const currentIconIndex = ref(0);
		const currentIcon = computed(() => {
			return themeIcons[currentIconIndex.value];
		});

		//切换login和register
		const isRegister = ref(false);
		const changeCard = () => {
			isRegister.value = !isRegister.value;
			clearForm();
		}

		const isBusy = ref(false);

		let locale = ref('ko');
		const translate = (key) => {
			if (!lang[key]) {
				return key;
			}
			if (locale.value != 'ko' && locale.value != 'ko-kr') {
				return lang[key].en;
			}
			return lang[key].ko;
		}

		watch(
			() => currentTheme.value,
			(newValue, oldValue) => {
				theme.global.name.value = 'light'
			}
		);

		const changeTheme = async () => {
			currentIconIndex.value = (currentIconIndex.value + 1) % 2;
			if (currentIconIndex.value == 0) {
				currentTheme.value = 'dark';
			} else {
				currentTheme.value = 'light';
			}
			// theme.global.name.value = currentTheme.value;
			if (await storageManager.hasItem('theme')) {
				await storageManager.updateItem('theme', currentTheme.value);
			} else {
				await storageManager.setItemWithExpiry('theme', currentTheme.value);
			}
		};

		// login
		const tab = ref(null);
		const loginFormRef = ref(null);
		const loadingForm = ref(false);
		const valid = ref(false);
		const loginForm = ref({
			otpserial: 1,
			userid: '',
			password: '',
			otpCode: '',
			isMaui: G_MAUI ? 'MAUI' : '',
			rememberMe: false,
		});
		const visiblePwd = ref(false);

		// register
		const registerForm = ref({
			userid: '',
			username: '',
			nickname: '',
			bankcode: '',
			accountno: '',
			password: '',
			tpassword: '',
			mobile: '',
			postcode: '',
			vertificate: '',
			joincode: '',
			email: '',
			address1: '',
			address2: '',
			isMaui: G_MAUI ? 'MAUI' : '',
		});
		const banks = ref([]);
		const token = ref('');
		const showPassword = ref(false);
		const showPassword1 = ref(false);
		const showPassword2 = ref(false);
		const isChecked1 = ref(false);
		const isChecked2 = ref(false);
		const isEnableID = ref(false);
		const isVertificate = ref(false);
		const isJoinCode = ref(false);
		const isSendCode = ref(false);
		const globalRealServer = ref(true);
		const globalVertificate = ref(0);
		const globalJoinCode = ref(0);
		const globalJoinCodeValue = ref('');
		const regloading = ref(false);
		const active = ref(0);
		const timeCount = ref(3);

		// rules
		
		const pwdRules = ref([value => {
			if (value) {
				return true
			}
			return translate('needPassword');
		}]);
		const useridLoginRules = ref([
			value => {
				if (value) {
					return true
				}
				return translate('needLoginID');
			}
		]);
		const useridRules = ref([
			value => {
				if (value) {
					if (value.length < 4) {
						return translate('needLoginIDRule');
					}
					else {
						return true
					}
				}
				return translate('needLoginID');
			}
		])
		const passwordRules = ref([
			value => {
				if (value) {
					if (value.length < 4) {
						return translate('needLoginPWRule');
					}
					else {
						return true;
					}
				}
				return translate('needPassword');
			}
		]);
		const tradePasswordRules = ref([
			value => {
				if (value) {
					if (value.length != 4) {
						return '거래비밀번호는 4자리로 입력해야 합니다!'
					}
					else {
						if (/^\d{4}$/.test(value)) {
							return true
						}
						else {
							return '거래비밀번호는 숫자로 입력하세요'
						}
					}
				}
				return '거래비밀번호를 입력하세요.'
			}
		])
		const usernameRules = ref([
			value => {
				if (value) return true
				return translate('needNameRule');
			}
		])
		const nicknameRules = ref([
			value => {
				if (value) return true
				return '닉네임을 입력하세요'
			}
		])
		const accountnoRules = ref([
			value => {
				if (value) return true
				return '계좌번호를 입력하세요'
			}
		])
		const bankcodeRules = ref([
			value => {
				if (value && value != '0' && value != 0) return true
				return '은행을 선택하세요!'
			}
		])
		const vertificateRules = ref([
			value => {
				if (value) {
					if (/^[0-9]{6}$/.test(value)) {
						return true
					}
					else {
						return '인증번호 형식이 아닙니다'
					}
				}
				else {
					return '인증번호를 입력하세요!'
				}

			}
		])
		const mobileRules = ref([
			value => {
				if (value) {
					if (/^010[0-9]{8}$/.test(value)) {
						return true
					}
					else {
						return '휴대전화번호가 올바르지 않습니다'
					}
				}
				return '휴대전화번호를 입력해 주세요.'
			}
		])
		const postcodeRules = ref([
			value => {
				if (value) {
					if (/^\d{5,6}$/.test(value)) {
						return true
					}
					else {
						return '5~6자리로 입력하세요'
					}
				}
				return true
			}
		])

		// 获取数据
		const getBanks = () => {
			$.ajax({
				type: "POST",
				url: "/User/GetBanks",
				dataType: "json",
				success: function (result) {
					if (result.code == "SUCCESS") {
						banks.value = JSON.parse(result.json)
					}
				},
				error: function (xhr, status, error) {
				}
			});
		}
		const handleChange = debounce((val) => {
			isEnableID.value = false;
		}, 300)
		const vertificateChange = debounce((val) => {
			isVertificate.value = false;
		}, 300)
		const joincodeChange = debounce((val) => {
			isJoinCode.value = false;
		}, 300)
		const mobileChange = debounce((val) => {
			isSendCode.value = false;
		}, 300)
		const clearForm = () => {
			isChecked1.value = false;
			isChecked2.value = false;
			isEnableID.value = false;
			isVertificate.value = false;
			isJoinCode.value = false;
			isSendCode.value = false;
			loginForm.value = {
				otpserial: 1,
				userid: '',
				password: '',
				otpCode: '',
				rememberMe: false,
				isMaui: G_MAUI ? 'MAUI' : '',
			};
			registerForm.value = {
				userid: '',
				username: '',
				nickname: '',
				bankcode: 0,
				accountno: '',
				password: '',
				mobile: '',
				postcode: '',
				vertificate: '',
				joincode: globalJoinCodeValue.value,
				email: '',
				address1: '',
				address2: '',
				isMaui: G_MAUI ? 'MAUI' : '',
			};
			active.value = 0;
			if (globalJoinCodeValue.value != '') {
				isJoinCode.value = true;
			}
			getCookie()
		}

		// login
		async function submitLogin() {
			if (loadingForm.value) return; // 엔터·버튼이 겹쳐 두 번 보내지 않도록
			loadingForm.value = true;
			const { valid } = await loginFormRef.value.validate();
			if (valid) {
				if (G_MAUI) {
					// 앱은 JSON 응답(접속 정보)이 필요해 기존 AJAX 로그인
					requestLogin(loginForm.value);
				} else {
					// 웹은 폼 POST(/User/LoginForm). 성공은 서버가 첫 화면으로, 실패는 /Login 으로 되돌려 사유를 보여 준다
					loginFormRef.value.$el.submit();
				}
			} else {
				loadingForm.value = false;
				layer.msg(translate('needLoginInfo'));
			}
		}


		const requestLogin = (form) => {
			$.post({
				url: '/User/Login',
				data: form,
				dataType: 'json',
				success: function (data) {
					if (data.code.toLowerCase() == 'success') {
						layer.msg(translate('loginSuccess'), { icon: 1, time: 1000 });
						setTimeout(() => {
							loadingForm.value = false;
							window.location.href = '/';
						}, 1000);
					} else {
						loadingForm.value = false;
						
						layer.msg(`${translate('loginFail')}，${translate(data.message)}`, { icon: 0, time: 1000 });
					}
				},
				error: function (xhr, status, error) {
					loadingForm.value = false;
					layer.msg(translate('loginFail'), { icon: 2, time: 1000 });
				},
			});
		};

		// register
		const changeStepper = (next) => {
			if (active.value == 0 && !isChecked1.value) {
				layer.msg(translate('needAgreement'), { icon: 7, time: 1000 })
				return;
			}
			if (active.value == 1 && (!isChecked1.value || !isChecked2.value)) {
				layer.msg(translate('needPrivacy'), { icon: 7, time: 1000 })
				return;
			}
			if (active.value == 2) {
				if (!isEnableID.value) {
					layer.msg(translate('needCheckID'), { icon: 7, time: 1000 });
					return false;
				}
				if (isEnableID.value.length < 4) {
					layer.msg(translate('needLoginIDRule'), { icon: 7, time: 1000 });
					return false;
				}
				if (!registerForm.value.password) {
					layer.msg(translate('needPassword'), { icon: 7, time: 1000 });
					return false;
				}
				if (registerForm.value.password.length < 4) {
					layer.msg(translate('needLoginPWRule'), { icon: 7, time: 1000 })
					return false;
				}
				if (!registerForm.value.mobile) {
					if (globalRealServer.value) {
						layer.msg("휴대전화번호를 입력하세요!", { icon: 7, time: 1000 })
						return false;
					}
				}
				if (globalRealServer.value) {
					if (!/^010[0-9]{8}$/.test(registerForm.value.mobile)) {
						layer.msg("휴대전화번호 형식이 올바르지 않습니다!", { icon: 7, time: 1000 })
						return false;
					}
				}

				if (globalVertificate.value > 0) {
					if (!isVertificate.value) {
						layer.msg('인증번호를 입력하세요!', { icon: 7, time: 1000 });
						return false;
					}
				}
				if (registerForm.value.joincode != '' && !isJoinCode.value) {
					layer.msg('초대코드를 인증하세요!', { icon: 7, time: 1000 });
					return false;
				}
				if (globalJoinCode.value == 2) {
					if (!isJoinCode.value) {
						layer.msg('초대코드를 입력하세요!', { icon: 7, time: 1000 });
						return false;
					}
				}
			}
			next();
		}
		const changeStepperSimul = (next) => {
			if (active.value == 0 && !isChecked1.value) {
				layer.msg(translate('needAgreement'), { icon: 7, time: 1000 })
				return;
			}
			if (active.value == 1 && (!isChecked1.value || !isChecked2.value)) {
				layer.msg(translate('needPrivacy'), { icon: 7, time: 1000 })
				return;
			}
			next();
		}

		// 核对ID是否重复
		function checkID() {			
			const userid = registerForm.value.userid;
			if (userid == '') {
				layer.msg(translate('needLoginID'), { icon: 2, time: 1000 })
				return false;
			}
			if (userid.length < 4) {
				layer.msg(translate('needLoginIDRule'), { icon: 2, time: 1000 })
				return false;
			}

			if (isBusy.value) {
				return false;
			}
			isBusy.value = true;

			$.ajax({
				type: "POST",
				url: "/User/CheckID",
				data: { userid: userid },
				dataType: "json",
				success: function (result) {
					isBusy.value = false;
					if (result.code == "SUCCESS") {
						layer.msg(translate('checkIDOK'), { icon: 1, time: 1000 });
						isEnableID.value = true;
					}
					else {
						layer.msg(translate('checkIDDup'), { icon: 2, time: 1000 })
					}
				},
				error: function (xhr, status, error) {
					isBusy.value = false;
					layer.msg(translate('restError'), { icon: 2, time: 1000 })
				}
			});
		}
		function sendVertificate() {
			const mobile = registerForm.value.mobile;
			if (mobile == '' || mobile == undefined) {
				layer.msg("휴대전화번호를 입력하세요!")
				return false;
			}
			else if (!/^010[0-9]{8}$/.test(mobile)) {
				layer.msg("휴대전화번호 형식이 올바르지 않습니다!")
				return false;
			}
			else {
				if (isBusy.value) {
					return false;
				}
				isBusy.value = true;
				$.ajax({
					type: "POST",
					url: "/Contact/SendSmsOTP",
					data: { mobile: mobile },
					dataType: "json",
					success: function (result) {
						isBusy.value = false;
						if (result.code == 0) {
							layer.msg(result.msg, { icon: 1, time: 1000 });
							token.value = result.token;
							isSendCode.value = true;
						}
						else {
							layer.msg(result.msg, { icon: 2, time: 1000 })
						}
					},
					error: function (xhr, status, error) {
						isBusy.value = false;
						layer.msg(translate('restError'), { icon: 2, time: 1000 })
					}
				});
			}
		}
		function checkVertificate() {
			const vertificate = registerForm.value.vertificate;
			const mobile = registerForm.value.mobile;
			if (mobile == '' || mobile == undefined) {
				layer.msg("휴대전화번호를 입력하세요!")
				return false;
			}
			else if (vertificate == '' || vertificate == undefined) {
				layer.msg("인증번호를 입력하세요!")
				return false;
			}
			else {
				if (isBusy.value) {
					return false;
				}
				isBusy.value = true;
				$.ajax({
					type: "POST",
					url: "/Contact/CheckSmsOTP",
					data: {
						smscode: vertificate,
						token: token.value,
						mobile: mobile
					},
					dataType: "json",
					success: function (result) {
						isBusy.value = false;

						if (result.code == 0) {
							layer.msg(result.msg, { icon: 1, time: 1000 });
							isVertificate.value = true;
						}
						else {
							layer.msg(result.msg, { icon: 2, time: 1000 })
						}
					},
					error: function (xhr, status, error) {
						isBusy.value = false;
						layer.msg(translate('restError'), { icon: 2, time: 1000 })
					}
				});
			}
		}
		function checkJoinCode() {
			const joincode = registerForm.value.joincode;
			if (joincode == '' || joincode == undefined) {
				layer.msg("초대코드를 입력하세요!")
				return false;
			}
			else {
				if (isBusy.value) {
					return false;
				}
				isBusy.value = true;
				$.ajax({
					type: "POST",
					url: "/User/CheckJoinCode",
					data: {
						joincode: joincode,
					},
					dataType: "json",
					success: function (result) {
						isBusy.value = false;

						if (result.code == 0) {
							layer.msg(result.msg, { icon: 1, time: 1000 });
							isJoinCode.value = true;
						}
						else {
							layer.msg(result.msg, { icon: 2, time: 1000 })
						}
					},
					error: function (xhr, status, error) {
						isBusy.value = false;
						layer.msg(translate('restError'), { icon: 2, time: 1000 })
					}
				});
			}
		}
		function handleRegister() {
			if (registerForm.value.username == '') {
				layer.msg(translate('needNameRule'), { icon: 2, time: 1000 })
				return false;
			}

			if (globalRealServer.value) {
				if (registerForm.value.bankcode == '' || registerForm.value.bankcode == '0' || registerForm.value.bankcode == 0) {
					layer.msg("은행을 선택하세요!", { icon: 2, time: 1000 })
					return false;
				}
				if (registerForm.value.accountno == '') {
					layer.msg("계좌번호를 입력하세요!", { icon: 2, time: 1000 })
					return false;
				}
			}

			if (isBusy.value) {
				return false;
			}

			isBusy.value = true;
			regloading.value = true;
			$.ajax({
				type: "POST",
				url: "/User/RegisterAction",
				data: {
					userid: registerForm.value.userid,
					username: registerForm.value.username,
					nickname: registerForm.value.username,
					bankcode: registerForm.value.bankcode,
					accountno: registerForm.value.accountno,
					password: registerForm.value.password,
					tpassword: '1234',
					mobile: registerForm.value.mobile,
					joincode: registerForm.value.joincode,
					postcode: registerForm.value.postcode,
					email: registerForm.value.email,
					address1: registerForm.value.address1,
					address2: registerForm.value.address2,
					isMaui: G_MAUI ? 'MAUI' : '',
				},
				dataType: "json",
				success: function (result) {
					isBusy.value = false;
					if (result.code == "SUCCESS") {
						layer.msg(translate('registSuccess'), { icon: 1, time: 1000 }, async () => {
							active.value = 4;
						})
					}
					else {
						layer.msg(translate('registFail'), { icon: 2, time: 1000 })
					}
					regloading.value = false;
				},
				error: function (xhr, status, error) {
					isBusy.value = false;
					layer.msg(translate('restError'), { icon: 2, time: 1000 })
					regloading.value = false;
				}
			});

			return false;
		}
		function handleRegisterSimul() {
			if (!isEnableID.value) {
				layer.msg(translate('needCheckID'), { icon: 7, time: 1000 });
				return false;
			}
			if (isEnableID.value.length < 4) {
				layer.msg(translate('needLoginIDRule'), { icon: 7, time: 1000 });
				return false;
			}
			if (!registerForm.value.password) {
				layer.msg(translate('needPassword'), { icon: 7, time: 1000 });
				return false;
			}
			if (registerForm.value.password.length < 4) {
				layer.msg(translate('needLoginPWRule'), { icon: 7, time: 1000 })
				return false;
			}
			if (!registerForm.value.mobile) {
				if (globalRealServer.value) {
					layer.msg("휴대전화번호를 입력하세요!", { icon: 7, time: 1000 })
					return false;
				}
			}
			if (globalRealServer.value) {
				if (!/^010[0-9]{8}$/.test(registerForm.value.mobile)) {
					layer.msg("휴대전화번호 형식이 올바르지 않습니다!", { icon: 7, time: 1000 })
					return false;
				}
			}

			if (globalVertificate.value > 0) {
				if (!isVertificate.value) {
					layer.msg('인증번호를 입력하세요!', { icon: 7, time: 1000 });
					return false;
				}
			}
			if (registerForm.value.joincode != '' && !isJoinCode.value) {
				layer.msg('초대코드를 인증하세요!', { icon: 7, time: 1000 });
				return false;
			}
			if (globalJoinCode.value == 2) {
				if (!isJoinCode.value) {
					layer.msg('초대코드를 입력하세요!', { icon: 7, time: 1000 });
					return false;
				}
			}
			if (registerForm.value.username == '') {
				layer.msg(translate('needNameRule'), { icon: 2, time: 1000 })
				return false;
			}

			if (globalRealServer.value) {
				if (registerForm.value.bankcode == '' || registerForm.value.bankcode == '0' || registerForm.value.bankcode == 0) {
					layer.msg("은행을 선택하세요!", { icon: 2, time: 1000 })
					return false;
				}
				if (registerForm.value.accountno == '') {
					layer.msg("계좌번호를 입력하세요!", { icon: 2, time: 1000 })
					return false;
				}
			}

			if (isBusy.value) {
				return false;
			}

			isBusy.value = true;
			regloading.value = true;
			$.ajax({
				type: "POST",
				url: "/User/RegisterAction",
				data: {
					userid: registerForm.value.userid,
					username: registerForm.value.username,
					nickname: registerForm.value.username,
					bankcode: registerForm.value.bankcode,
					accountno: registerForm.value.accountno,
					password: registerForm.value.password,
					tpassword: '1234',
					mobile: registerForm.value.mobile,
					joincode: registerForm.value.joincode,
					postcode: registerForm.value.postcode,
					email: registerForm.value.email,
					address1: registerForm.value.address1,
					address2: registerForm.value.address2,
					isMaui: G_MAUI ? 'MAUI' : '',
				},
				dataType: "json",
				success: function (result) {
					isBusy.value = false;
					if (result.code == "SUCCESS") {
						layer.msg(translate('registSuccess'), { icon: 1, time: 1000 }, async () => {
							changeCard();
						})
					}
					else {
						layer.msg(translate('registFail'), { icon: 2, time: 1000 })
					}
					regloading.value = false;
				},
				error: function (xhr, status, error) {
					isBusy.value = false;
					layer.msg(translate('restError'), { icon: 2, time: 1000 })
					regloading.value = false;
				}
			});

			return false;
		}
		function getCookie() {
			const cookieValue = cookies.get('MyLoginID')
			if (cookieValue != null) {
				loginForm.value.userid = cookieValue;
				loginForm.value.rememberMe = true;
			}
		}
		function tabClick() {
			if (tab.value == 'loginTab') {
				//$('.rounded-xl').css('width', '380px');
				//$('.rounded-xl').css('width', '380px');
				$('#app_layout > div > main > div > div').attr('style', 'width: 25%!important');
			}
			else {
				$('#app_layout > div > main > div > div').attr('style', 'width: 40%!important');
			}
		}

		onMounted(async () => {
			if (loginErrorMessage) {
				if (loginErrorUserId) loginForm.value.userid = loginErrorUserId;
				layer.msg(`${translate('loginFail')}，${translate(loginErrorMessage)}`, { icon: 0, time: 1500 });
			}
			globalVertificate.value = g_Vertificate;
			globalJoinCode.value = g_JoinCode;
			globalJoinCodeValue.value = g_JoinCodeValue;
			globalRealServer.value = !G_MAUI;

			if (!globalRealServer.value) {
				locale.value = navigator.language.toLowerCase();
			}

			//初始化主题
			if (await storageManager.hasItem('theme')) {
				currentTheme.value = await storageManager.getItem('theme');
			}
			if (currentTheme.value === 'auto') {
				currentIconIndex.value = 2;
				const currentHour = new Date().getHours();
				if (currentHour >= 6 && currentHour < 18) {
					theme.global.name.value = 'light';
					logoUrl.value = '../images/logo_white.png';
				} else {
					theme.global.name.value = 'dark';
					logoUrl.value = '../images/logo_dark.png';
				}
			} else {
				currentIconIndex.value = currentTheme.value == 'light' ? 1 : 0;
				logoUrl.value = currentTheme.value === 'light' ? '../images/logo_white.png' : '../images/logo_dark.png';
				theme.global.name.value = currentTheme.value;
			}

			getBanks();
			getCookie();
		});
		watch(() => tab.value,
			(newvalue, oldvalue) => {
				clearForm();
			}
		)

		return {
			isRegister,
			changeCard,

			logoUrl,
			currentTheme,
			themeIcons,
			currentIcon,
			changeTheme,
			isMobile,
			tab,
			loginFormRef,
			loginForm,
			loadingForm,
			valid,
			visiblePwd,
			pwdRules,
			submitLogin,
			tabClick,

			// stepper
			active,
			isChecked1,
			isChecked2,
			changeStepper,
			changeStepperSimul,

			// register
			timeCount,
			showPassword,
			showPassword1,
			showPassword2,
			banks,
			globalVertificate,
			globalJoinCode,
			globalJoinCodeValue,
			registerForm,
			regloading,
			handleRegister,
			handleRegisterSimul,

			isJoinCode,
			isSendCode,
			isVertificate,
			isEnableID,

			sendVertificate,
			checkVertificate,
			checkID,
			checkJoinCode,

			vertificateChange,
			joincodeChange,
			mobileChange,
			handleChange,

			// rules
			useridRules,
			useridLoginRules,
			passwordRules,
			mobileRules,
			postcodeRules,
			usernameRules,
			nicknameRules,
			accountnoRules,
			vertificateRules,
			bankcodeRules,
			tradePasswordRules,
			visibleDownload,
			closeDownload,
			translate,
		};
	},
})
	.use(vuetify)
	.directive('comma', vComma)
	.mount('#app');
